import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { BillEorCodesResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-codes-response';
import { BillEorFooterResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-footer-response';
import { BillEorHeaderResponse } from '../../../../models/api/view/claim/financials/bills/bill-eor-header-response';
import { BillLinesResponse } from '../../../../models/api/view/claim/financials/bills/bill-lines-response';
import { ClaimBillDetail } from '../../../../models/api/view/claim/financials/bills/claim-bill-detail';
import { Payment } from '../../../../models/api/view/claim/financials/payment-history/payment';
import { FormatClaimPipe } from '../../../../pipes/format-claim.pipe';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { BillDataTransformer } from '../../../../utilities/bill-data-transformer';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { ToggleSwitchComponent } from '../../../core/controls/toggle-switch.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { ToastComponent } from '../../../core/labels/toast.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { LoadingState } from '../../../core/panels/loading-panel.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { TogglePanelComponent } from '../../../core/panels/toggle-panel.component';
import { BillInfoSectionComponent } from './bill-info-section.component';
import { BillLinesSectionComponent } from './bill-lines-section.component';
import { EorDetailSectionComponent } from './eor-detail-section.component';
import { EorExplanationSectionComponent } from './eor-explanation-section.component';
import { EorReprintModalComponent } from './eor-reprint-modal.component';
import { EorServiceDetailComponent } from './eor-service-detail.component';
import { InvoiceBillService } from './invoice-bill.service';
import { PaymentInfoSectionComponent } from './payment-info-section.component';

@Component({
  selector: 'app-invoice-bill',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PageHeaderComponent,
    TabPanelComponent,
    TabComponent,
    ButtonComponent,
    ToastComponent,
    TogglePanelComponent,
    BillInfoSectionComponent,
    PaymentInfoSectionComponent,
    BillLinesSectionComponent,
    EorDetailSectionComponent,
    EorServiceDetailComponent,
    EorExplanationSectionComponent,
    ToggleSwitchComponent,
    ActionRowComponent,
    EorReprintModalComponent,
    EmptyStateComponent,
    ProgressbarComponent,
  ],
  providers: [InvoiceBillService, FormatClaimPipe],
  template: `
    <app-page-header
      label="Bill/Invoice detail"
      [backLabel]="backLabel"
      [backRouterLink]="backRouterLink"
    />
    <app-tab-panel
      class="tw-grow"
      classes="tw-min-h-[360px]"
      (valueChange)="onTabChange($event)"
    >
      <app-tab label="Bill/Invoice detail">
        <ng-template #content>
          <div class="tw-flex tw-gap-4">
            <div class="tw-basis-full">
              <div class="tw-flex tw-flex-row tw-w-full">
                <div
                  class="tw-flex tw-flex-col tw-w-1/2 tw-gap-0 tw-pb-2 tw-m-0"
                >
                  <app-toast>
                    Reproduction of EOR data should not be provided to providers
                    without first contacting claim operations.
                  </app-toast>
                </div>
                <div class="tw-flex tw-w-1/2 tw-justify-end">
                  <app-button
                    emphasis="High"
                    [disabled]="!canRequestEorReprint"
                    labelCase="none"
                    label="Request EOR reprint"
                    (click)="onRequestEorReprint()"
                  />
                </div>
              </div>
              <app-bill-info-section [billItem]="billItem" [state]="state" />
            </div>
          </div>
          <div class="tw-flex tw-gap-4 tw-pt-8">
            <div class="tw-basis-full">
              <app-payment-info-section
                [paymentData]="paymentData"
                [state]="paymentLoadingState"
              />
            </div>
          </div>
          <div class="tw-flex tw-gap-4 tw-pt-8">
            <div class="tw-basis-full">
              <app-bill-lines-section
                [billLinesData]="billLinesData"
                [state]="billLinesLoadingState"
                (export)="onExportLines($event)"
              />
            </div>
          </div>
        </ng-template>
      </app-tab>
      <app-tab label="EOR bill review">
        <ng-template #content>
          <div class="tw-flex tw-flex-row tw-w-full">
            <div class="tw-flex tw-flex-col tw-w-1/2 tw-gap-0 tw-pb-2 tw-m-0">
              <app-toast>
                Reproduction of this document does not confirm payment. The
                review details and explanations are not in a jurisdictionally
                compliant format. This is not an EOR.
              </app-toast>
            </div>
            <div class="tw-flex tw-w-1/2 tw-justify-end">&nbsp;</div>
          </div>
          @if (eorLoadingState === 'Loading') {
            <app-empty-state
              height="400px"
              icon="fa-light fa-file-medical"
              label="Loading EOR bill review"
              message="Retrieving from bill repository"
            >
              <div class="tw-py-4">
                <app-progressbar />
              </div>
            </app-empty-state>
          } @else {
            <div class="tw-flex tw-flex-col tw-w-full">
              <div
                class="tw-flex tw-justify-between tw-items-center tw-w-full tw-pb-2"
              >
                <div></div>
                <app-action-row>
                  <app-toggle-switch
                    label="Expand all"
                    [control]="eorExpandAll"
                    [showHelpText]="false"
                    (click)="toggleEorSections()"
                    right1
                  />
                </app-action-row>
              </div>

              <app-toggle-panel [showExpand]="false">
                <app-eor-detail-section
                  #eorDetail="eorDetailSection"
                  [billItem]="billItem"
                  [billEorHeader]="billEorHeader"
                  [claimNumber]="currentClaimNumber"
                />
                <app-eor-service-detail
                  #eorService="eorServiceDetail"
                  [billLinesData]="billLinesData"
                  [billEorCodes]="billEorCodes"
                  (export)="onExportEorLines($event)"
                />
                <app-eor-explanation-section
                  #eorExplanation="eorExplanation"
                  [billEorFooter]="billEorFooter"
                />
              </app-toggle-panel>
            </div>
          }
        </ng-template>
      </app-tab>
    </app-tab-panel>

    <!-- EOR Reprint Processing Modal -->
    <app-eor-reprint-modal
      [visible]="showEorReprintModal"
      [loading]="eorReprintModalLoading"
      [error]="eorReprintModalError"
      (close)="closeEorReprintModal()"
    />
  `,
  styles: ``,
})
export class InvoiceBillComponent extends BaseComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private invoiceBillService = inject(InvoiceBillService);
  private snackbar = inject(Snackbar);
  private formatClaimPipe = inject(FormatClaimPipe);

  // Display flags
  disability = false;
  workersComp = false;
  backLabel = '';
  backRouterLink = '';

  // Data properties
  paymentData: Payment | null = null;
  billItem: ClaimBillDetail | null = null;
  billLinesData: BillLinesResponse | null = null;
  billEorHeader: BillEorHeaderResponse | null = null;
  billEorFooter: BillEorFooterResponse | null = null;
  billEorCodes: BillEorCodesResponse | null = null;
  currentClaimNumber = '';

  // Loading states
  state: LoadingState = 'Loading';
  paymentDataLoaded = false;
  paymentLoadingState: LoadingState = 'Loading';
  eorDataLoaded = false;
  eorLoadingState: LoadingState = 'Init';
  billLinesLoaded = false;
  billLinesLoadingState: LoadingState = 'Loading';

  // EOR expand all toggle
  eorExpandAll = new FormControl<boolean>(false);

  // EOR reprint modal state
  showEorReprintModal = false;
  eorReprintModalError: string | null = null;
  eorReprintModalLoading = false;

  // View references to EOR child components
  @ViewChild('eorDetail') eorDetailComponent?: EorDetailSectionComponent;
  @ViewChild('eorService') eorServiceComponent?: EorServiceDetailComponent;
  @ViewChild('eorExplanation')
  eorExplanationComponent?: EorExplanationSectionComponent;

  get billLines() {
    return BillDataTransformer.getBillLines(this.billLinesData);
  }

  get canRequestEorReprint(): boolean {
    return (
      this.state === 'Loaded' &&
      this.billItem?.billStatus?.toUpperCase() !== 'PENDING'
    );
  }

  private setupBackNavigation(): void {
    const rawBackLabel = this.route.snapshot.queryParamMap.get('backLabel');
    const rawBackRouterLink =
      this.route.snapshot.queryParamMap.get('backRouterLink');

    // Whitelist allowed back navigation routes to prevent open redirect
    const allowedRoutes = [
      '../payment-history',
      '../invoice-list',
      '../financials',
    ];

    this.backLabel =
      rawBackLabel && rawBackLabel.length <= 100 ? rawBackLabel : 'Back';

    // Handle back navigation - compute proper path
    const backRoute = allowedRoutes.includes(rawBackRouterLink || '')
      ? rawBackRouterLink!
      : '../invoice-list';

    // Get current URL and compute absolute path for back navigation
    // Current route is at /financials/invoice-bill/:icn
    // We need to navigate to sibling routes like /financials/invoice-list
    const currentUrl = this.router.url;
    const segments = currentUrl.split('/');
    // Remove query params if any
    if (segments.length > 0 && segments[segments.length - 1].includes('?')) {
      segments[segments.length - 1] =
        segments[segments.length - 1].split('?')[0];
    }

    // Remove the last two segments (invoice-bill and :icn value)
    segments.pop(); // Remove ICN value
    segments.pop(); // Remove 'invoice-bill'

    // Construct the base path
    const basePath = segments.join('/');

    // Convert relative back routes to absolute paths
    if (backRoute === '../invoice-list') {
      this.backRouterLink = `${basePath}/invoice-list`;
    } else if (backRoute === '../payment-history') {
      this.backRouterLink = `${basePath}/payment-history`;
    } else if (backRoute === '../financials') {
      this.backRouterLink = basePath; // Just go to the financials base
    } else {
      this.backRouterLink = `${basePath}/invoice-list`; // Default fallback
    }
  }

  async ngOnInit() {
    const claim = this.view.currentClaim();
    if (!claim) {
      this.router.navigate(['/view']);
      return;
    }

    this.state = 'Loading';
    this.setupBackNavigation();

    this.currentClaimNumber = this.formatClaimPipe.transform(
      claim.fileNumber,
      claim.fileNumFormat,
    );
    this.disability = claim.lineOfBusiness === 'DS';
    this.workersComp = claim.lineOfBusiness === 'WC';

    const icn = this.route.snapshot.paramMap.get('icn');
    if (!icn) {
      this.view.hideLoading();
      return;
    }

    const claimKey: ClaimKey = {
      recordSource: claim.recordSource,
      recordId: claim.recordId,
    };
    const billResponse = await this.invoiceBillService.getClaimBill(
      claimKey,
      icn,
    );
    this.billItem = billResponse?.bill || null;

    // Load related data in parallel (non-blocking)
    this.loadPaymentData();
    this.loadBillLines();
    this.loadEorData();

    this.state = 'Loaded';
    this.view.hideLoading();
  }

  onTabChange(tabLabel: string) {
    if (tabLabel === 'EOR bill review' && !this.eorDataLoaded) {
      this.loadEorData();
    }
  }

  toggleEorSections() {
    const expanded = this.eorExpandAll.value || false;
    this.eorDetailComponent?.toggleCards(expanded);
    this.eorServiceComponent?.toggleCards(expanded);
    this.eorExplanationComponent?.toggleCards(expanded);
  }

  async loadPaymentData() {
    if (this.paymentDataLoaded) return;
    const claim = this.view.currentClaim();
    const paymentUid = this.billItem?.paymentUid;
    if (!claim || !paymentUid) {
      this.paymentLoadingState = 'Empty';
      return;
    }
    const claimKey: ClaimKey = {
      recordSource: claim.recordSource,
      recordId: claim.recordId,
    };
    this.paymentData = await this.invoiceBillService.getPaymentData(
      claimKey,
      paymentUid,
    );
    this.paymentDataLoaded = true;
    this.paymentLoadingState = this.paymentData ? 'Loaded' : 'Empty';
  }

  async loadBillLines() {
    if (this.billLinesLoaded) return;
    const claim = this.view.currentClaim();
    const billUid = this.billItem?.billUid;
    if (!claim || !billUid) {
      this.billLinesLoadingState = 'Empty';
      return;
    }
    const claimKey: ClaimKey = {
      recordSource: claim.recordSource,
      recordId: claim.recordId,
    };
    this.billLinesData = await this.invoiceBillService.getBillLines(
      claimKey,
      billUid,
    );
    this.billLinesLoaded = true;
    this.billLinesLoadingState = this.billLinesData ? 'Loaded' : 'Empty';
  }

  async loadEorData() {
    if (this.eorDataLoaded || this.eorLoadingState === 'Loading') return;
    this.eorLoadingState = 'Loading';
    const claim = this.view.currentClaim();
    const billUid = this.billItem?.billUid;
    if (!claim || !billUid) {
      this.eorLoadingState = 'Empty';
      return;
    }
    const claimKey: ClaimKey = {
      recordSource: claim.recordSource,
      recordId: claim.recordId,
    };
    const eorData = await this.invoiceBillService.getEorData(claimKey, billUid);
    this.billEorHeader = eorData.header;
    this.billEorFooter = eorData.footer;
    this.billEorCodes = eorData.codes;
    this.eorDataLoaded = true;
    this.eorLoadingState =
      eorData.header || eorData.footer || eorData.codes ? 'Loaded' : 'Empty';
  }

  async onExportLines(format: ExportFormat) {
    const claim = this.view.currentClaim();
    await this.invoiceBillService.exportBillLines(
      this.billLinesData,
      {
        claimNumber: claim.claimNumber,
        fileNumber: claim.fileNumber,
        claimantFirstName: claim.claimantFirstName,
        claimantLastName: claim.claimantLastName,
        accountName: claim.accountName,
      },
      {
        contractNumber: this.billItem?.contractNumber,
        contractName: this.billItem?.contractName,
        icn: this.billItem?.icn,
        invoice: this.billItem?.invoice,
      },
      format,
      'BillDetail',
    );
  }

  async onExportEorLines(format: ExportFormat) {
    const claim = this.view.currentClaim();
    await this.invoiceBillService.exportBillLines(
      this.billLinesData,
      {
        claimNumber: claim.claimNumber,
        fileNumber: claim.fileNumber,
        claimantFirstName: claim.claimantFirstName,
        claimantLastName: claim.claimantLastName,
        accountName: claim.accountName,
      },
      {
        contractNumber: this.billItem?.contractNumber,
        contractName: this.billItem?.contractName,
        icn: this.billItem?.icn,
        invoice: this.billItem?.invoice,
      },
      format,
      'BillReview',
    );
  }

  async onRequestEorReprint(): Promise<void> {
    const validation = this.invoiceBillService.validateSingleBillEligibility(
      this.billItem,
    );
    if (!validation.valid) {
      this.snackbar.show(validation.message!, 'Error');
      return;
    }

    this.showEorReprintModal = true;
    this.eorReprintModalLoading = true;
    this.eorReprintModalError = null;

    const claim = this.view.currentClaim();
    const claimKey: ClaimKey = {
      recordSource: claim.recordSource,
      recordId: claim.recordId,
    };
    const filename = this.billItem?.icn
      ? `EOR_${this.billItem.icn}.pdf`
      : undefined;

    const result = await this.invoiceBillService.processEorReprint(
      claimKey,
      [this.billItem],
      filename,
    );

    if (result.success) {
      this.snackbar.show(
        'Your EOR has been downloaded successfully.',
        'Success',
      );
      this.closeEorReprintModal();
    } else {
      this.eorReprintModalLoading = false;
      this.eorReprintModalError =
        result.error || 'An error occurred while requesting the EOR.';
    }
  }

  closeEorReprintModal(): void {
    this.showEorReprintModal = false;
    this.eorReprintModalError = null;
    this.eorReprintModalLoading = false;
  }
}
